import type { Aabb, Vec2, WorldManifest } from "@townsy/contracts";
import { RectGrid, collidersNear, type ColliderSet } from "./spatialIndex";

/**
 * Shared drivable cars (founder feedback round 3, ADR-0006). Pure geometry and a deterministic
 * kinematic model used by the realtime server (authority) and by the client's drive prediction.
 *
 * Conventions (same as the rest of @townsy/world): metres, x east, z north, heading = yaw with
 * 0 = +z (north) and +PI/2 = +x (east). A car's FORWARD is (sin h, cos h) and its RIGHT is
 * (cos h, -sin h); its LEFT is the opposite of right. Traffic drives on the right, so the driver
 * sits on the LEFT (heading +z => the driver's door is on the -x side).
 */

export interface VehicleSpec {
  /** Body style index (0 sedan, 1 hatchback, 2 van, 3 pickup), the same order as the renderer's models. */
  style: number;
  /** Half the body length including bumpers (along the heading). */
  halfLength: number;
  /** Half the body width including wheel hubs (across the heading). */
  halfWidth: number;
  /** Distance between the axles (kinematic bicycle model). */
  wheelbase: number;
  /** Where the front seats are, measured forward from the body centre (driver's door / seat anchor). */
  seatForward: number;
  /** How far left of the centre line the driver sits (the renderer's CarSpec.seat.x = -seatLeft). */
  seatLeft: number;
}

/**
 * Measured from the four low-poly bodies in apps/web/src/scene/town/cars.ts (model +z = front):
 *  - sedan: body 4.3 m, bumpers to +-2.24, hubs to +-0.955, axles +-1.35, cabin -1.35..0.95;
 *  - hatchback: body 3.8 m, bumpers to +-1.99, hubs to +-0.935, axles +-1.2, cabin -1.8..0.75;
 *  - van: body 4.6 m, bumpers to +-2.39, hubs to +-0.995, axles +-1.5, cab 1.35..1.95;
 *  - pickup: body 4.8 m, bumpers to +-2.49, hubs to +-0.975, axles -1.55/+1.45, cab -0.2..1.3.
 * Values are rounded UP a little so the collider never sits inside the visible body. The seat matches
 * the renderer's CarSpec.seat (round 3 cars.ts) so the door point, the seated driver and the name tag line up.
 */
export const VEHICLE_SPECS: readonly VehicleSpec[] = [
  { style: 0, halfLength: 2.25, halfWidth: 0.96, wheelbase: 2.7, seatForward: 0.2, seatLeft: 0.38 },
  { style: 1, halfLength: 2.0, halfWidth: 0.94, wheelbase: 2.4, seatForward: 0.05, seatLeft: 0.37 },
  { style: 2, halfLength: 2.4, halfWidth: 1.0, wheelbase: 3.0, seatForward: 1.1, seatLeft: 0.42 },
  { style: 3, halfLength: 2.5, halfWidth: 0.98, wheelbase: 3.0, seatForward: 0.45, seatLeft: 0.4 },
];

/** The spec for a style index; out-of-range or non-integer styles fall back to the sedan. */
export function vehicleSpec(style: number): VehicleSpec {
  const i = Number.isInteger(style) && style >= 0 && style < VEHICLE_SPECS.length ? style : 0;
  return VEHICLE_SPECS[i]!;
}

export interface VehiclePose {
  x: number;
  z: number;
  /** Yaw, 0 = +z (north). */
  heading: number;
}

export interface VehicleMotion extends VehiclePose {
  /** Metres per second along the heading; negative while reversing. */
  speed: number;
}

/**
 * Driving model constants (ADR-0006: 12 m/s forward, 4 m/s reverse).
 *  - accel: forward/reverse acceleration at full throttle (m/s^2);
 *  - brake: deceleration while the throttle opposes the motion (m/s^2);
 *  - coastDecel: deceleration with no throttle (m/s^2);
 *  - maxSteerRad: front wheel angle at full steer and walking speed (scaled down with speed);
 *  - minTurnSpeed: below this speed the car does not rotate, and a car slower than this counts as
 *    STOPPED (getting in requires it).
 */
export const DRIVE_CONFIG = {
  maxForward: 12,
  maxReverse: 4,
  accel: 5,
  brake: 10,
  coastDecel: 2.5,
  maxSteerRad: 0.6,
  minTurnSpeed: 0.3,
} as const;

/** Steering is divided by (1 + |speed| / STEER_FADE_SPEED): full lock when crawling, gentle at speed. */
export const STEER_FADE_SPEED = 8;
/** |throttle| below this is "no throttle" (coasting). */
const THROTTLE_DEADZONE = 0.05;
/** How far outside the body the door / exit points are (a walker of radius 0.4 then clears it by 0.3 m). */
export const DOOR_STANDOFF = 0.7;
/** Exit points behind / in front of the car stand this far beyond the bumper. */
const END_STANDOFF = 0.8;

// ---------------------------------------------------------------------------------------------
// Oriented boxes
// ---------------------------------------------------------------------------------------------

/** Oriented box on the ground plane: centre, half extents across (width) and along (length) the heading. */
export interface Obb {
  cx: number;
  cz: number;
  halfWidth: number;
  halfLength: number;
  heading: number;
}

export function vehicleObb(style: number, pose: VehiclePose): Obb {
  const s = vehicleSpec(style);
  return { cx: pose.x, cz: pose.z, halfWidth: s.halfWidth, halfLength: s.halfLength, heading: pose.heading };
}

function axes(heading: number): { fx: number; fz: number; rx: number; rz: number } {
  const s = Math.sin(heading);
  const c = Math.cos(heading);
  return { fx: s, fz: c, rx: c, rz: -s };
}

/** The four corners: front-right, front-left, rear-left, rear-right. */
export function obbCorners(o: Obb): [Vec2, Vec2, Vec2, Vec2] {
  const { fx, fz, rx, rz } = axes(o.heading);
  const at = (f: number, r: number): Vec2 => ({ x: o.cx + fx * f + rx * r, z: o.cz + fz * f + rz * r });
  return [at(o.halfLength, o.halfWidth), at(o.halfLength, -o.halfWidth), at(-o.halfLength, -o.halfWidth), at(-o.halfLength, o.halfWidth)];
}

/** Axis-aligned bounds of an oriented box. */
export function obbBounds(o: Obb): Aabb {
  const c = obbCorners(o);
  const xs = c.map((p) => p.x);
  const zs = c.map((p) => p.z);
  return { minX: Math.min(...xs), minZ: Math.min(...zs), maxX: Math.max(...xs), maxZ: Math.max(...zs) };
}

/** A point in the box's local frame: `f` along the heading, `r` to the right. */
function toLocal(o: Obb, x: number, z: number): { f: number; r: number } {
  const { fx, fz, rx, rz } = axes(o.heading);
  const dx = x - o.cx;
  const dz = z - o.cz;
  return { f: dx * fx + dz * fz, r: dx * rx + dz * rz };
}

function toWorld(o: Obb, f: number, r: number): Vec2 {
  const { fx, fz, rx, rz } = axes(o.heading);
  return { x: o.cx + fx * f + rx * r, z: o.cz + fz * f + rz * r };
}

/** True when the point is inside the box (closed). */
export function pointInObb(x: number, z: number, o: Obb): boolean {
  const l = toLocal(o, x, z);
  return Math.abs(l.f) <= o.halfLength && Math.abs(l.r) <= o.halfWidth;
}

/** True when a circle overlaps the box (touching does not count). */
export function circleIntersectsObb(x: number, z: number, r: number, o: Obb): boolean {
  const l = toLocal(o, x, z);
  const cf = Math.max(-o.halfLength, Math.min(o.halfLength, l.f));
  const cr = Math.max(-o.halfWidth, Math.min(o.halfWidth, l.r));
  const df = l.f - cf;
  const dr = l.r - cr;
  return df * df + dr * dr < r * r;
}

/** Tiny extra push so a resolved circle is clearly outside (not touching within rounding). */
const PUSH_EPS = 1e-6;

/**
 * Where a circle overlapping the box must move to just touch it from outside: along the line from
 * the closest box point when the centre is outside, otherwise out through the nearest face. Returns
 * null when the circle does not overlap the box.
 */
export function pushCircleOutOfObb(x: number, z: number, r: number, o: Obb): Vec2 | null {
  if (!circleIntersectsObb(x, z, r, o)) return null;
  const l = toLocal(o, x, z);
  const inside = Math.abs(l.f) <= o.halfLength && Math.abs(l.r) <= o.halfWidth;
  if (!inside) {
    const cf = Math.max(-o.halfLength, Math.min(o.halfLength, l.f));
    const cr = Math.max(-o.halfWidth, Math.min(o.halfWidth, l.r));
    const df = l.f - cf;
    const dr = l.r - cr;
    const d = Math.hypot(df, dr);
    return toWorld(o, cf + (df / d) * (r + PUSH_EPS), cr + (dr / d) * (r + PUSH_EPS));
  }
  // Centre inside: leave through the nearest face (ties prefer the sides, i.e. the doors).
  const toSide = o.halfWidth - Math.abs(l.r);
  const toEnd = o.halfLength - Math.abs(l.f);
  if (toSide <= toEnd) {
    const sign = l.r >= 0 ? 1 : -1;
    return toWorld(o, l.f, sign * (o.halfWidth + r + PUSH_EPS));
  }
  const sign = l.f >= 0 ? 1 : -1;
  return toWorld(o, sign * (o.halfLength + r + PUSH_EPS), l.r);
}

function project(points: readonly Vec2[], ax: number, az: number): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (const p of points) {
    const v = p.x * ax + p.z * az;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  return [lo, hi];
}

/** Separating-axis overlap test of two convex quads over the given axes (touching does not count). */
function quadsOverlap(a: readonly Vec2[], b: readonly Vec2[], axesList: readonly [number, number][]): boolean {
  for (const [ax, az] of axesList) {
    const [a0, a1] = project(a, ax, az);
    const [b0, b1] = project(b, ax, az);
    if (a1 <= b0 + 1e-9 || b1 <= a0 + 1e-9) return false;
  }
  return true;
}

function aabbCornersOf(b: Aabb): Vec2[] {
  return [
    { x: b.minX, z: b.minZ },
    { x: b.maxX, z: b.minZ },
    { x: b.maxX, z: b.maxZ },
    { x: b.minX, z: b.maxZ },
  ];
}

/** True when an oriented box and an axis-aligned box overlap (touching does not count). */
export function obbIntersectsAabb(o: Obb, b: Aabb): boolean {
  const { fx, fz, rx, rz } = axes(o.heading);
  return quadsOverlap(obbCorners(o), aabbCornersOf(b), [
    [1, 0],
    [0, 1],
    [fx, fz],
    [rx, rz],
  ]);
}

/** True when two oriented boxes overlap (touching does not count). */
export function obbIntersectsObb(a: Obb, b: Obb): boolean {
  const A = axes(a.heading);
  const B = axes(b.heading);
  return quadsOverlap(obbCorners(a), obbCorners(b), [
    [A.fx, A.fz],
    [A.rx, A.rz],
    [B.fx, B.fz],
    [B.rx, B.rz],
  ]);
}

// ---------------------------------------------------------------------------------------------
// Doors, getting in and out
// ---------------------------------------------------------------------------------------------

/** A point beside / in front of / behind the car: `forward` along the heading, `left` to the driver's side. */
function carPoint(pose: VehiclePose, forward: number, left: number): Vec2 {
  const { fx, fz, rx, rz } = axes(pose.heading);
  return { x: pose.x + fx * forward - rx * left, z: pose.z + fz * forward - rz * left };
}

/**
 * Where a walker stands to get in: on the LEFT side (traffic drives on the right), level with the
 * front seat, DOOR_STANDOFF (0.7 m) outside the body.
 */
export function driverDoorPoint(style: number, pose: VehiclePose): Vec2 {
  const s = vehicleSpec(style);
  return carPoint(pose, s.seatForward, s.halfWidth + DOOR_STANDOFF);
}

/** Where the driver sits (an actor's position while driving). */
export function driverSeatPoint(style: number, pose: VehiclePose): Vec2 {
  const s = vehicleSpec(style);
  return carPoint(pose, s.seatForward, s.seatLeft);
}

/**
 * Places to put a driver who gets out, in order of preference: the driver's door, the passenger
 * door, behind the car, in front of it. The caller picks the first one that is free.
 */
export function exitCandidates(style: number, pose: VehiclePose): Vec2[] {
  const s = vehicleSpec(style);
  return [
    driverDoorPoint(style, pose),
    carPoint(pose, s.seatForward, -(s.halfWidth + DOOR_STANDOFF)),
    carPoint(pose, -(s.halfLength + END_STANDOFF), 0),
    carPoint(pose, s.halfLength + END_STANDOFF, 0),
  ];
}

/** A car slower than this is stopped (getting in is allowed). */
export const STOPPED_SPEED = DRIVE_CONFIG.minTurnSpeed;

/**
 * True when a walker at `walker` may get into the car: within `maxDoorDistance` of the driver's door,
 * on the driver's side, and the car is stopped (|speed| < 0.3 m/s). Occupancy, rate limits and
 * whether the walker already drives are the server's checks.
 */
/** How far from the driver's door point a walker may be to get in (the client prompt uses exactly this). */
export const CAR_DOOR_REACH_METRES = 1.4;

export function canEnterVehicle(walker: Vec2, style: number, motion: VehicleMotion, maxDoorDistance = CAR_DOOR_REACH_METRES): boolean {
  if (!Number.isFinite(walker.x) || !Number.isFinite(walker.z)) return false;
  if (!(Math.abs(motion.speed) < STOPPED_SPEED)) return false;
  const door = driverDoorPoint(style, motion);
  if (Math.hypot(walker.x - door.x, walker.z - door.z) > maxDoorDistance) return false;
  // Driver's side only: the walker's offset to the LEFT of the centre line must be positive.
  const { rx, rz } = axes(motion.heading);
  const left = -((walker.x - motion.x) * rx + (walker.z - motion.z) * rz);
  return left > 0;
}

// ---------------------------------------------------------------------------------------------
// Where cars may be
// ---------------------------------------------------------------------------------------------

/**
 * Road and crosswalk surfaces: the union cars must stay inside. World v6 (ADR-0042): the whole manifest gives the whole
 * town's roads (Market Street runs on into River Street and High Street, so a car can drive anywhere); a district
 * slice gives that district's (v5: the whole manifest meant Central).
 */
export function drivableRects(manifest: WorldManifest): Aabb[] {
  return manifest.surfaces.filter((s) => s.kind === "road" || s.kind === "crosswalk").map((s) => s.aabb);
}

export interface DriveEnv {
  /** Road + crosswalk rects (drivableRects). */
  drivable: readonly Aabb[];
  /** Static colliders (buildings, props, decor, homes, yard items...): a list or a ColliderIndex of it (same result). */
  colliders: ColliderSet;
  /** Every OTHER car (never the car being stepped). */
  cars: readonly Obb[];
  /** Walker centres (actors on foot). */
  walkers: readonly Vec2[];
  walkerRadius: number;
}

function inAnyRect(x: number, z: number, rects: readonly Aabb[]): boolean {
  for (const b of rects) if (x >= b.minX - 1e-9 && x <= b.maxX + 1e-9 && z >= b.minZ - 1e-9 && z <= b.maxZ + 1e-9) return true;
  return false;
}

function strictlyInsideRect(x: number, z: number, b: Aabb): boolean {
  return x > b.minX && x < b.maxX && z > b.minZ && z < b.maxZ;
}

/**
 * Outline vertices of the union of the drivable rects (rect corners not strictly inside another
 * rect). If one of them lies strictly inside a car's box, the box pokes out of the union across a
 * concave corner (e.g. cutting the sidewalk corner at a junction) even though its own corners are on
 * the road.
 */
function outlineVertices(rects: readonly Aabb[]): Vec2[] {
  const out: Vec2[] = [];
  for (const b of rects) {
    for (const p of aabbCornersOf(b)) {
      if (!rects.some((o) => strictlyInsideRect(p.x, p.z, o))) out.push(p);
    }
  }
  return out;
}

/** The drivable rects with their outline vertices, both indexed (world v3: cost independent of the road count). */
interface DrivableIndex {
  rects: readonly Aabb[];
  rectGrid: RectGrid;
  outline: Vec2[];
  outlineGrid: RectGrid;
}

/** Cached by array identity: pass the same drivable array every step (drivableRects once per room). */
const drivableCache = new WeakMap<readonly Aabb[], DrivableIndex>();

function drivableIndex(rects: readonly Aabb[]): DrivableIndex {
  let v = drivableCache.get(rects);
  if (!v) {
    const outline = outlineVertices(rects);
    v = {
      rects,
      rectGrid: new RectGrid(rects),
      outline,
      outlineGrid: new RectGrid(outline.map((p) => ({ minX: p.x, minZ: p.z, maxX: p.x, maxZ: p.z }))),
    };
    drivableCache.set(rects, v);
  }
  return v;
}

/** Perimeter sample spacing for the on-road test (metres). */
const EDGE_SAMPLE = 0.5;

/**
 * How far a box is off the road: the number of perimeter samples (corners + every <= 0.5 m) outside
 * the drivable rects plus the number of road-outline vertices inside the box. 0 = fully on the road.
 */
export function obbOffRoad(o: Obb, drivable: readonly Aabb[]): number {
  let bad = 0;
  const c = obbCorners(o);
  // Only the rects and outline vertices near the box can matter (same count as testing them all).
  const idx = drivableIndex(drivable);
  const pad = 1e-6;
  const minX = Math.min(c[0].x, c[1].x, c[2].x, c[3].x) - pad;
  const maxX = Math.max(c[0].x, c[1].x, c[2].x, c[3].x) + pad;
  const minZ = Math.min(c[0].z, c[1].z, c[2].z, c[3].z) - pad;
  const maxZ = Math.max(c[0].z, c[1].z, c[2].z, c[3].z) + pad;
  const rects = idx.rectGrid.queryIndices(minX, minZ, maxX, maxZ).map((i) => idx.rects[i]!);
  for (let i = 0; i < 4; i++) {
    const a = c[i]!;
    const b = c[(i + 1) % 4]!;
    const n = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.z - a.z) / EDGE_SAMPLE));
    for (let k = 0; k < n; k++) {
      const t = k / n;
      if (!inAnyRect(a.x + (b.x - a.x) * t, a.z + (b.z - a.z) * t, rects)) bad++;
    }
  }
  const shrunk: Obb = { ...o, halfWidth: o.halfWidth - 1e-6, halfLength: o.halfLength - 1e-6 };
  for (const i of idx.outlineGrid.queryIndices(minX, minZ, maxX, maxZ)) {
    const v = idx.outline[i]!;
    if (pointInObb(v.x, v.z, shrunk)) bad++;
  }
  return bad;
}

/** True when the whole box lies inside the union of the drivable rects. */
export function obbOnDrivable(o: Obb, drivable: readonly Aabb[]): boolean {
  return obbOffRoad(o, drivable) === 0;
}

/** True when the box overlaps a static collider, another car or a walker. */
function poseBlocked(o: Obb, env: DriveEnv): boolean {
  // Bounding-box prefilter (the street has well over a thousand static boxes in v3; the exact tests
  // allocate). With a ColliderIndex only the boxes near the car are visited at all.
  const reach = Math.hypot(o.halfWidth, o.halfLength);
  const minX = o.cx - reach;
  const maxX = o.cx + reach;
  const minZ = o.cz - reach;
  const maxZ = o.cz + reach;
  for (const b of collidersNear(env.colliders, minX, minZ, maxX, maxZ)) {
    if (b.maxX <= minX || b.minX >= maxX || b.maxZ <= minZ || b.minZ >= maxZ) continue;
    if (obbIntersectsAabb(o, b)) return true;
  }
  for (const c of env.cars) {
    const cr = Math.hypot(c.halfWidth, c.halfLength);
    if (Math.abs(c.cx - o.cx) >= reach + cr || Math.abs(c.cz - o.cz) >= reach + cr) continue;
    if (obbIntersectsObb(o, c)) return true;
  }
  const wr = env.walkerRadius;
  for (const w of env.walkers) {
    if (Math.abs(w.x - o.cx) >= reach + wr || Math.abs(w.z - o.cz) >= reach + wr) continue;
    if (circleIntersectsObb(w.x, w.z, wr, o)) return true;
  }
  return false;
}

function finitePose(pose: VehiclePose): boolean {
  return Number.isFinite(pose.x) && Number.isFinite(pose.z) && Number.isFinite(pose.heading);
}

/**
 * A pose is valid when the car's box is entirely on the road / crosswalks and overlaps no static
 * collider, no other car and no walker.
 */
export function isPoseValid(style: number, pose: VehiclePose, env: DriveEnv): boolean {
  if (!finitePose(pose)) return false;
  const o = vehicleObb(style, pose);
  return obbOnDrivable(o, env.drivable) && !poseBlocked(o, env);
}

// ---------------------------------------------------------------------------------------------
// Driving
// ---------------------------------------------------------------------------------------------

export interface DriveInput {
  /** +1 accelerate forward, -1 brake then reverse once stopped (InputDrive.throttle). */
  throttle: number;
  /** +1 turn right (clockwise from above), -1 left (InputDrive.steer). */
  steer: number;
}

function clampUnit(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return v < -1 ? -1 : v > 1 ? 1 : v;
}

function normalizeHeading(a: number): number {
  let r = a % (Math.PI * 2);
  if (r > Math.PI) r -= Math.PI * 2;
  if (r <= -Math.PI) r += Math.PI * 2;
  return r;
}

/** New speed after one step of throttle (InputDrive semantics). */
export function nextSpeed(speed: number, throttle: number, dt: number): number {
  const cfg = DRIVE_CONFIG;
  const t = clampUnit(throttle);
  let v = Number.isFinite(speed) ? speed : 0;
  if (Math.abs(t) < THROTTLE_DEADZONE) {
    const d = cfg.coastDecel * dt;
    v = Math.abs(v) <= d ? 0 : v - Math.sign(v) * d;
  } else if (t > 0) {
    // Forward: brake out of a reverse first, then accelerate.
    v = v < 0 ? Math.min(0, v + cfg.brake * t * dt) : v + cfg.accel * t * dt;
  } else if (v > 0) {
    // Braking while rolling forward stops at 0; reversing starts on a later step.
    v = Math.max(0, v + cfg.brake * t * dt);
  } else {
    v = v + cfg.accel * t * dt;
  }
  return Math.max(-cfg.maxReverse, Math.min(cfg.maxForward, v));
}

/** Front wheel angle for a steer input at a speed (scaled down as the car goes faster). */
export function steerAngle(steer: number, speed: number): number {
  return (clampUnit(steer) * DRIVE_CONFIG.maxSteerRad) / (1 + Math.abs(speed) / STEER_FADE_SPEED);
}

/** Crawl speed kept when only a rotation is possible (scraping off a wall). */
const SCRAPE_SPEED = 1;

/**
 * Advance a driven car by one input over `dt` seconds. Pure and deterministic (the server is the
 * authority; the client predicts with the same function). Kinematic bicycle model: the heading turns
 * at speed * tan(wheel angle) / wheelbase and the car moves along the mid-step heading.
 *
 * It NEVER returns an invalid pose (isPoseValid) from a valid one: when the full step is invalid it
 * tries, in order, sliding along the x or the z axis (the roads are axis-aligned, so this slides along
 * a curb) with and without the rotation, the translation alone, then the rotation alone (at a crawl);
 * if nothing is valid the car keeps its pose and stops (speed 0).
 *
 * Recovery: a traffic car stopped in a tight U-turn can have its rear over the curb (the lane loops'
 * U-turns are 1.5 m arcs). Started from such a pose (off the road but touching nothing), a move is
 * accepted when it touches nothing and is no further off the road (obbOffRoad), so the driver can
 * steer back onto the road. A starting pose that overlaps a collider, car or walker never moves
 * (speed 0); the server pushes walkers out of cars.
 */
export function stepVehicle(m: VehicleMotion, style: number, input: DriveInput, dt: number, env: DriveEnv): VehicleMotion {
  const step = Math.min(Math.max(Number.isFinite(dt) ? dt : 0, 0), 0.25);
  const spec = vehicleSpec(style);
  const speed = nextSpeed(m.speed, input.throttle, step);
  const stay: VehicleMotion = { x: m.x, z: m.z, heading: m.heading, speed: 0 };
  if (!finitePose(m)) return stay;
  const startBox = vehicleObb(style, m);
  if (poseBlocked(startBox, env)) return stay;
  const offLimit = obbOffRoad(startBox, env.drivable);
  // Recovery is only for a car whose centre is on the road (a U-turn overhang), never for one that is off it.
  if (offLimit > 0 && !inAnyRect(m.x, m.z, env.drivable)) return stay;
  const acceptable = (p: VehiclePose): boolean => {
    if (!finitePose(p)) return false;
    const o = vehicleObb(style, p);
    if (offLimit > 0 && !inAnyRect(p.x, p.z, env.drivable)) return false;
    return obbOffRoad(o, env.drivable) <= offLimit && !poseBlocked(o, env);
  };
  if (step === 0) return { x: m.x, z: m.z, heading: m.heading, speed };

  const turning = Math.abs(speed) >= DRIVE_CONFIG.minTurnSpeed;
  const dH = turning ? (speed * Math.tan(steerAngle(input.steer, speed)) * step) / spec.wheelbase : 0;
  const mid = m.heading + dH / 2;
  const dist = speed * step;
  const dx = Math.sin(mid) * dist;
  const dz = Math.cos(mid) * dist;
  const heading = normalizeHeading(m.heading + dH);
  if (dist === 0 && dH === 0) return { x: m.x, z: m.z, heading: m.heading, speed };

  const full = Math.hypot(dx, dz);
  const scaled = (moved: number): number => (full > 0 ? speed * (moved / full) : 0);
  const xFirst = Math.abs(dx) >= Math.abs(dz);
  const slides: { x: number; z: number; moved: number }[] = xFirst
    ? [
        { x: m.x + dx, z: m.z, moved: Math.abs(dx) },
        { x: m.x, z: m.z + dz, moved: Math.abs(dz) },
      ]
    : [
        { x: m.x, z: m.z + dz, moved: Math.abs(dz) },
        { x: m.x + dx, z: m.z, moved: Math.abs(dx) },
      ];
  const candidates: VehicleMotion[] = [{ x: m.x + dx, z: m.z + dz, heading, speed }];
  for (const s of slides) if (s.moved > 1e-9 && dH !== 0) candidates.push({ x: s.x, z: s.z, heading, speed: scaled(s.moved) });
  if (dH !== 0 && full > 1e-9) candidates.push({ x: m.x + dx, z: m.z + dz, heading: m.heading, speed });
  for (const s of slides) if (s.moved > 1e-9) candidates.push({ x: s.x, z: s.z, heading: m.heading, speed: scaled(s.moved) });
  if (dH !== 0) candidates.push({ x: m.x, z: m.z, heading, speed: Math.sign(speed) * Math.min(Math.abs(speed), SCRAPE_SPEED) });

  for (const c of candidates) {
    if (acceptable(c)) return c;
  }
  return stay;
}
